import { AfterViewInit, Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { ActivatedRoute, Router } from '@angular/router'
import { Observable, Subject, combineLatest, firstValueFrom, from } from 'rxjs'
import { map, takeUntil } from 'rxjs/operators'

import { AsiakkaanTuote, LaskunAlv, LaskunTyypit } from '@lemon/core-firebase/model/lasku'
import { LaskuUriService } from '@lemon/core-firebase/service/lasku/lasku-uri.service'
import { CurrencyService } from '@lemon/core/service/currency.service'

import { AsiakasService } from 'app/_angular/service/asiakas/asiakas.service'
import { LaskutusHakuStateService } from 'app/_jaettu-angular/laskut/haku/laskutus-haku-state.service'
import { AlvService } from 'app/_jaettu-angular/service/alv.service'
import { DebugService } from 'app/_angular/service/debug.service'
import { KirjautunutKayttajaService } from 'app/_angular/service/kirjautunut-kayttaja.service'
import { Asiakas } from 'app/_jaettu-lemonator/model/asiakas'

import { TuotteetSelaaDataSourceService } from './tuotteet-selaa.datasource.service'

interface NaytettavaTuote extends AsiakkaanTuote {
  alvProsentti: number | null
  hintaVerollinen: number | null
}

@Component({
  templateUrl: './tuotteet-selaa.component.html',
  styleUrls: ['./tuotteet-selaa.component.css'],
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [TuotteetSelaaDataSourceService],
})
export class TuotteetSelaaComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  private _ngUnsubscribe = new Subject<void>()

  readonly asiakas = signal<Asiakas | null>(null)
  readonly hakuAvoinna = signal(false)

  rivienMaaraObservable: Observable<number>
  lataaObservable: Observable<boolean>
  naytettavatTuotteetObservable: Observable<NaytettavaTuote[]>
  kirjanpitajaOnDevaajaObservable: Observable<boolean>
  laskuTuotteetUriObservable: Observable<string>
  laskuTuotteetEncodedUriObservable: Observable<string>

  displayedColumns = ['nimi', 'yksikko', 'hinta', 'hintaVerollinen', 'alv']

  constructor(
    private _router: Router,
    private _route: ActivatedRoute,
    private _asiakasService: AsiakasService,
    private _hakuStateService: LaskutusHakuStateService,
    private _laskuUriService: LaskuUriService,
    private _debugService: DebugService,
    private _kirjautunutKayttajaService: KirjautunutKayttajaService,
    private _alvService: AlvService,
    private _currencyService: CurrencyService,
    public dataSource: TuotteetSelaaDataSourceService,
  ) {
    this.rivienMaaraObservable = this.dataSource.rivienMaaraObservable
    this.lataaObservable = this.dataSource.lataaObservable
    const kotimaanAlvtObservable = from(this._alvService.annaLaskutyypinAlvt(LaskunTyypit.TAVALLINEN, null))
    this.naytettavatTuotteetObservable = combineLatest([this.dataSource.nakyvatTuotteetObservable, kotimaanAlvtObservable]).pipe(
      map(([tuotteet, kotimaanAlvt]) => tuotteet.map(tuote => this._annaNaytettavaTuote(tuote, kotimaanAlvt))),
    )
  }

  ngOnInit() {
    if (this._hakuStateService.tuote.onkoHakutilaTallennettu(this._router.url)) {
      this.hakuAvoinna.set(true)
    }

    const initial = this.dataSource.sort
    this.sort.sort({ id: initial.active, start: initial.direction === 'asc' ? 'asc' : 'desc', disableClear: true })

    this.kirjanpitajaOnDevaajaObservable = this._kirjautunutKayttajaService.kirjanpitajaOnDevaajaObservable
    this.laskuTuotteetUriObservable = this._asiakasService.nykyinenAsiakasAvainObservable.pipe(
      map(asiakas => (asiakas?.avain ? this._laskuUriService.getLaskuTuotteetCollectionUri(asiakas.avain) : '')),
    )
    this.laskuTuotteetEncodedUriObservable = this.laskuTuotteetUriObservable.pipe(map(uri => this._debugService.createFirestoreLinkLemonaid(uri)))

    this._asiakasService.nykyinenAsiakasObservable.pipe(takeUntil(this._ngUnsubscribe)).subscribe(asiakas => {
      this.asiakas.set(asiakas)
    })
  }

  avaaHaku() {
    this.hakuAvoinna.set(true)
  }

  suljeHaku() {
    this.hakuAvoinna.set(false)
  }

  ngAfterViewInit() {
    this.sort.sortChange.pipe(takeUntil(this._ngUnsubscribe)).subscribe(sort => this.dataSource.setSort(sort))
    this.paginator.page.pipe(takeUntil(this._ngUnsubscribe)).subscribe(page => this.dataSource.setPage(page))
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  private _annaNaytettavaTuote(tuote: AsiakkaanTuote, kotimaanAlvt: LaskunAlv[]): NaytettavaTuote {
    const alvTunniste = tuote.alv?.[LaskunTyypit.TAVALLINEN.avain]
    const alv = kotimaanAlvt.find(kotimaanAlv => kotimaanAlv.tunniste === alvTunniste)
    const alvProsentti = alv?.prosentti ?? null
    const hintaJaAlvTiedossa = alvProsentti !== null && this._currencyService.onkoNumero(tuote.hinta)
    const hintaVerollinen = hintaJaAlvTiedossa ? this._currencyService.roundHalfUp(tuote.hinta + this._currencyService.annaAlvMaaraAlvittomastaHinnasta(tuote.hinta, alvProsentti), 2) : null
    return { ...tuote, alvProsentti, hintaVerollinen }
  }

  async avaa(tuote: AsiakkaanTuote) {
    const avainTiedot = await firstValueFrom(this._asiakasService.nykyinenAsiakasAvainObservable)
    if (!avainTiedot?.avain) {
      return
    }
    this._router.navigate(['asiakkaat', avainTiedot.avain, 'tuotteet', 'selaa', tuote.$key])
  }
}
